import { comparer, IReactionDisposer, reaction, toJS } from 'mobx'
import type { ComputeFunction } from './Types/FieldTypeEnum'

/**
 * Визначення обчислювального поля
 */
interface ComputedFieldDefinition {
    /** Шлях до поля (наприклад: ['products', '0', 'total']) */
    path: string[]
    /** Функція обчислення */
    computeFn: ComputeFunction
    /** Шляхи до полів-залежностей */
    dependencies: string[][]
}

/**
 * Менеджер для обчислювальних полів форми
 * Автоматично відстежує зміни в полях-залежностях та перераховує значення
 */
export class ComputedFieldManager {
    private formContext: any
    private computedFields: Map<string, ComputedFieldDefinition> = new Map()
    private reactions: Map<string, IReactionDisposer> = new Map()

    constructor(formContext: any) {
        this.formContext = formContext
    }

    /**
     * Реєструє обчислювальне поле
     */
    registerComputedField(
        path: string[],
        computeFn: ComputeFunction,
        dependencies: string[]
    ): void {
        const pathKey = path.join('.')
        
        // Конвертуємо імена залежностей в шляхи
        const dependencyPaths = dependencies.map(dep => {
            // Якщо залежність відносна (без крапок), додаємо батьківський шлях
            if (!dep.includes('.')) {
                // Для полів всередині TABLE рядка
                const parentPath = path.slice(0, -1) // Видаляємо останній елемент (ім'я поля)
                return [...parentPath, dep]
            }
            // Якщо абсолютна - розбиваємо по крапці
            return dep.split('.')
        })

        const definition: ComputedFieldDefinition = {
            path,
            computeFn,
            dependencies: dependencyPaths,
        }

        this.computedFields.set(pathKey, definition)
        this.createReaction(pathKey, definition)
    }

    /**
     * Створює MobX reaction для відстеження змін
     */
    private createReaction(pathKey: string, definition: ComputedFieldDefinition): void {
        // Видаляємо попередню reaction якщо є
        this.disposeReaction(pathKey)

        const dispose = reaction(
            // Функція відстеження: отримуємо значення всіх залежностей
            () => {
                const values: Record<string, any> = {}
                
                definition.dependencies.forEach((depPath) => {
                    // Отримуємо ім'я поля (останній елемент шляху)
                    const fieldName = depPath[depPath.length - 1]
                    const value = this.formContext.getValue(depPath)
                    
                    // Для масивів та об'єктів створюємо глибоку копію через toJS
                    // щоб MobX міг відстежувати зміни всередині структур
                    values[fieldName] = Array.isArray(value) || (value && typeof value === 'object' && value.constructor === Object)
                        ? toJS(value)
                        : value
                })

                return values
            },
            // Функція ефекту: обчислюємо нове значення
            (values) => {
                try {
                    const newValue = definition.computeFn(values, this.formContext)
                    const currentValue = this.formContext.getValue(definition.path)
                    
                    // Оновлюємо тільки якщо значення змінилось
                    if (currentValue !== newValue) {
                        this.formContext.setValue(definition.path, newValue)
                    }
                } catch (error) {
                    console.error(`Error computing field ${pathKey}:`, error)
                }
            },
            {
                fireImmediately: true, // Виконати відразу при створенні
                delay: 0, // Без затримки
                equals: comparer.structural, // Структурне порівняння для масивів та об'єктів
            }
        )

        this.reactions.set(pathKey, dispose)
    }

    /**
     * Видаляє reaction для поля
     */
    private disposeReaction(pathKey: string): void {
        const dispose = this.reactions.get(pathKey)
        if (dispose) {
            dispose()
            this.reactions.delete(pathKey)
        }
    }

    /**
     * Видаляє обчислювальне поле
     */
    unregisterComputedField(path: string[]): void {
        const pathKey = path.join('.')
        this.disposeReaction(pathKey)
        this.computedFields.delete(pathKey)
    }

    /**
     * Перераховує всі обчислювальні поля
     */
    recomputeAll(): void {
        this.computedFields.forEach((definition, pathKey) => {
            // Пересоздаем reaction чтобы форсировать пересчет
            this.createReaction(pathKey, definition)
        })
    }

    /**
     * Видаляє всі reactions
     */
    dispose(): void {
        this.reactions.forEach(dispose => dispose())
        this.reactions.clear()
        this.computedFields.clear()
    }

    /**
     * Отримує список всіх обчислювальних полів
     */
    getComputedFields(): string[] {
        return Array.from(this.computedFields.keys())
    }
}
